import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import styled, { useTheme } from 'styled-components';
import { Translate } from 'react-localize-redux';

import {
  UploadImageRequest,
  UploadImageResponse,
} from '@services/merch/types/api/upload-image';

import ApiError from '@common/types/api/api-error';

import { AssetFile } from '@common/types/data/asset-file';
import { ImageData } from '@webapp/types/common/image-data';

import { promiseDelay } from '@webapp/utils/promise-delay';
import { upload, AssetUploadResponse } from '@webapp/sdk/assets';

import { isFulfilled } from './utils';

import { UpdateMerchImages } from '@webapp/store/merch/action-creators';
import { ImageWithPositionPlaceHolder } from '@webapp/store/merch/state';

import ManageArtMerch from '@webapp/controllers/merch/merch-design-manage-art';

import Upload from '@components/upload';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import SquareImg from '@components/basics/artwork-img';
import UploadItem from '@components/upload/upload-item';
import CoverArtIcon from '@components/basics/icons/cover-art';
import UploadListItem from '@components/upload/upload-list-item';

interface UploadFile {
  status: string;
  progress: number;
  imageUrl: string;
  filename: string;
  error: string;
  file: File;
}

const readImage = (f: File): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && e.target.result) {
        const img = new Image();
        const src = e.target.result.toString();

        img.onload = () => {
          resolve({
            src,
            width: img.width,
            height: img.height,
            type: f.type,
          });
        };

        img.onerror = () => reject(new Error('Error while loading file'));
        img.src = src;
      }
    };
    reader.readAsDataURL(f);
  });
};

const validateImageFormat = (file: File) => {
  if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
    throw new Error('UPLOAD.MERCH.INVALID_FORMAT');
  }
};

const validateImageSize = (image: ImageData) => {
  if (
    image.width < 2000 ||
    image.height < 2000 ||
    image.width > 6000 ||
    image.height > 6000 ||
    image.width !== image.height
  ) {
    throw new Error('UPLOAD.MERCH.INVALID_SIZE');
  }
};

interface ImagesState {
  imageUrl: string;
  progress: number;
  status: string;
  filename: string;
  error: string;
  file: File | undefined;
  newRequest?: AssetUploadResponse;
  id?: string;
  key?: string;
  url?: string;
}

interface UploadMerchArtCtrlProps {
  className?: string;
  imgUrl?: string[];
  onError: (errorId: string) => void;
  onErrorMessage: (message: string) => void;
  onChange: (assets?: (AssetFile | undefined)[], id?: string) => void;
  onUpload: (fileKey: string, fileName: string) => void;
  multipleFiles: number;
  activeImages?: Partial<
    UploadImageRequest &
      ImageWithPositionPlaceHolder &
      UploadImageResponse &
      Partial<AssetFile>
  >[];
  updateMerchImages: (
    images: Partial<
      UploadImageRequest &
        UploadImageResponse &
        ImageWithPositionPlaceHolder &
        Partial<AssetFile>
    >[]
  ) => void;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateMerchImages: (
    images: Partial<
      UploadImageRequest &
        UploadImageResponse &
        ImageWithPositionPlaceHolder &
        Partial<AssetFile>
    >[]
  ) => dispatch(new UpdateMerchImages(images)),
});

export const UploadMerchArtCtrl: React.FC<UploadMerchArtCtrlProps> = (
  props
) => {
  const theme = useTheme();

  const [status, setStatus] = React.useState<'uploading' | 'done'>();
  const [filename, setFilename] = React.useState('');
  const [error, setError] = React.useState('');
  const [errorImgFormat, setErrorImgFormat] = React.useState(false);
  const [errorImgSize, setErrorImgSize] = React.useState(false);
  const [abortControllers, setAbortControllers] = React.useState<
    Record<string, AbortController>
  >({});

  const [imageView, setImageView] = React.useState('upload');
  const [images, setImages] = React.useState<(ImagesState | undefined)[]>(
    props.activeImages?.map((image) => {
      return {
        id: image.id,
        imageUrl: image.url || '',
        progress: 0,
        status: 'done',
        filename: image.fileName || '',
        error: '',
        file: undefined,
        key: image.key,
      };
    }) || []
  );
  const imagesRef = React.useRef(images);

  React.useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  React.useEffect(() => {
    resetImagesToProps(props.activeImages);
  }, [props.activeImages]);

  const resetImagesToProps = (
    activeImages?: Partial<
      UploadImageRequest &
        ImageWithPositionPlaceHolder &
        UploadImageResponse &
        Partial<AssetFile>
    >[]
  ) => {
    setImages(
      activeImages?.map((image) => {
        return {
          id: image.id,
          imageUrl: image.url || '',
          progress: 100,
          status: 'done',
          filename: image.fileName || '',
          error: '',
          file: undefined,
          key: image.key,
        };
      }) || []
    );
  };

  const handleUploadFiles = async (files: FileList | File[]) => {
    if (images.length + files.length > props.multipleFiles) {
      props.onErrorMessage(
        `You cannot upload more than ${props.multipleFiles} image(s).`
      );
      return;
    }
    setError('');
    setErrorImgFormat(false);
    setErrorImgSize(false);
    props.onError('');
    props.onErrorMessage('');
    let uploadFiles: UploadFile[] = [];
    const abortControllersLocal: Record<string, AbortController> = {};
    try {
      if (files.length > 0) {
        for (const file of files as File[]) {
          const abortController = new AbortController();
          validateImageFormat(file);
          const image = await readImage(file);
          validateImageSize(image);
          abortControllersLocal[file.name] = abortController;

          uploadFiles.push({
            status: 'uploading',
            progress: 0,
            imageUrl: image.src,
            filename: file.name,
            error: '',
            file: file,
          });
        }
        setAbortControllers(abortControllersLocal);
        setStatus('uploading');
        setImages([...images, ...uploadFiles]);
        const result = uploadFiles?.map(async (image) => {
          if (image?.file) {
            const abortControllerSignal =
              abortControllersLocal[image.filename]?.signal;
            const newRequest = await upload(
              'merch-assets',
              image.file,
              abortControllerSignal
            );

            abortControllerSignal.onabort = function () {
              newRequest.upload.cancel();
            };

            await newRequest.upload.request;
            updateImageUploadProgress(image.imageUrl, 40);
            await promiseDelay(2000);
            updateImageUploadProgress(image.imageUrl, 60);

            return {
              ...image,
              progress: 80,
              status: 'done',
              newRequest,
              url: newRequest.url,
            };
          }
        });
        Promise.allSettled(result)
          .then((results) =>
            results
              .filter(isFulfilled)
              .map((result) => result.value)
              .filter((result) => !!result)
          )
          .then((values) => {
            if (values?.length) {
              const newImages = [...images, ...values];
              setStatus('done');
              setImages(newImages);

              if (newImages.length === props.multipleFiles) {
                handleChangeArtView('manage');
              }
              const assets = values
                .filter((value) => !!value)
                .map((value) => ({
                  id: value!.newRequest?.id,
                  contentType: value!.file?.type,
                  size: value!.file?.size || 0,
                  crc32: '',
                  url: value!.url,
                  key: value!.newRequest?.key,
                  created_at: new Date(),
                  updated_at: new Date(),
                }));
              props.onChange(assets);
              if (props.onUpload) {
                if (values && values.length > 0)
                  values?.forEach(async (image) => {
                    if (image && image.newRequest && image.filename) {
                      await props.onUpload(
                        image?.newRequest?.key,
                        image?.filename
                      );
                    }
                  });
              }
            }
          })
          .catch((err) => {
            console.log('handle file:', err);
          })
          .finally(() => {
            setAbortControllers({});
          });
      }
    } catch (e) {
      switch ((e as ApiError).message) {
        case 'UPLOAD.MERCH.INVALID_SIZE': {
          setErrorImgSize(true);
          break;
        }
        case 'UPLOAD.MERCH.INVALID_FORMAT': {
          setErrorImgFormat(true);
          break;
        }
        default: {
          setError(e ? (e as ApiError).message : 'RELEASE.UNKNOWN_ERROR');
        }
      }
      props.onError(e ? (e as ApiError).message : 'RELEASE.UNKNOWN_ERROR');
    }
  };

  const handleRemove = (image: ImagesState) => {
    const filteredImages = props.activeImages?.filter(
      (activeImage) => activeImage.id !== image.id
    );
    if (filteredImages) {
      props.updateMerchImages(filteredImages);
    } else {
      console.log('handleRemove error: ');
    }
  };

  const handleReplace = async (
    files: FileList | File[],
    image: ImagesState
  ) => {
    setError('');
    setErrorImgFormat(false);
    setErrorImgSize(false);
    props.onError('');
    props.onErrorMessage('');
    let uploadFiles: UploadFile[] = [];
    const abortControllersLocal: Record<string, AbortController> = {};
    try {
      if (files.length > 0) {
        for (const file of files as File[]) {
          const abortController = new AbortController();
          validateImageFormat(file);
          const img = await readImage(file);
          validateImageSize(img);
          abortControllersLocal[file.name] = abortController;

          uploadFiles.push({
            status: 'uploading',
            progress: 0,
            imageUrl: img.src,
            filename: file.name,
            error: '',
            file: file,
          });
        }
        setAbortControllers(abortControllersLocal);
        setStatus('uploading');
        setImages([uploadFiles[0]]);
        const result = uploadFiles?.map(async (image) => {
          if (image?.file) {
            const abortControllerSignal =
              abortControllersLocal[image.filename]?.signal;
            const newRequest = await upload(
              'merch-assets',
              image.file,
              abortControllerSignal
            );

            abortControllerSignal.onabort = function () {
              newRequest.upload.cancel();
            };

            await newRequest.upload.request;
            updateImageUploadProgress(image.imageUrl, 40);
            await promiseDelay(2000);
            updateImageUploadProgress(image.imageUrl, 60);

            return {
              ...image,
              progress: 80,
              status: 'done',
              newRequest,
              url: newRequest.url,
            };
          }
        });
        Promise.all(result)
          .then((values) => {
            handleRemove(image);
            if (values?.length) {
              setStatus('done');
              setImages([values[0]]);
              const assets = values
                .filter((value) => !!value)
                .map((value: any) => ({
                  id: value!.newRequest?.id,
                  contentType: value!.file?.type,
                  size: value!.file?.size || 0,
                  crc32: '',
                  url: value!.newRequest?.url,
                  key: value!.newRequest?.key,
                  created_at: new Date(),
                  updated_at: new Date(),
                }));
              props.onChange(assets, image?.key);
              if (props.onUpload) {
                if (values && values.length > 0)
                  values?.forEach(async (image: any) => {
                    if (image && image.newRequest && image.filename) {
                      await props.onUpload(
                        image?.newRequest?.key,
                        image?.filename
                      );
                    }
                  });
              }
            }
          })
          .catch((err) => {
            console.log('handle replace: ', err);
          })
          .finally(() => {
            setAbortControllers({});
          });
      }
    } catch (e) {
      switch ((e as ApiError).message) {
        case 'UPLOAD.MERCH.INVALID_SIZE': {
          setErrorImgSize(true);
          break;
        }
        case 'UPLOAD.MERCH.INVALID_FORMAT': {
          setErrorImgFormat(true);
          break;
        }
        default: {
          setError(e ? (e as ApiError).message : 'RELEASE.UNKNOWN_ERROR');
        }
      }
      props.onError(e ? (e as ApiError).message : 'RELEASE.UNKNOWN_ERROR');
    }
  };

  const onCancel = (imgUrl?: string) => {
    if (imgUrl) {
      const image = images.find((img) => img?.imageUrl === imgUrl);
      const newImage = images.filter((img) => {
        if (img) {
          if (
            img?.filename === image?.filename &&
            img?.imageUrl === image?.imageUrl
          ) {
            const abortController = abortControllers[image.filename];
            if (abortController) {
              abortController.abort();
            }
            return false;
          }
        }
        return true;
      });
      if (newImage.length) {
        setImages(newImage);
      } else {
        resetImagesToProps(props.activeImages);
      }
    }
    setStatus(undefined);
    setFilename('');
  };

  const updateImageUploadProgress = (imgUrl?: string, progress?: number) => {
    if (imgUrl) {
      const image = imagesRef.current?.find((img) => img?.imageUrl === imgUrl);
      if (image && progress) {
        const newImage = imagesRef.current?.map((img) => {
          if (
            img &&
            img.filename === image?.filename &&
            img.imageUrl === image?.imageUrl
          ) {
            return { ...img, progress };
          }
          return img;
        });
        setImages(newImage);
      }
    }
  };

  const handleChangeArtView = (view: string) => {
    setImageView(view);
  };

  return (
    <div className={props.className}>
      <Wrapper gap={20}>
        <ManageText
          className={imageView === 'upload' ? 'active' : ''}
          type="big-title"
          customFont="semi-bold"
          onClick={() => handleChangeArtView('upload')}
        >
          <Translate id="UPLOAD.ART" />
        </ManageText>
        <ManageText
          className={`${imageView === 'manage' ? 'active' : ''} ${
            !props.activeImages?.length ? 'disabled' : ''
          }`}
          type="big-title"
          customFont="semi-bold"
          onClick={() => handleChangeArtView('manage')}
        >
          <Translate id="MERCH.SECTION.DESIGN.MANAGE.ART" />
        </ManageText>
      </Wrapper>
      {imageView === 'upload' ? (
        <div>
          <Upload
            status={status}
            error={error || errorImgSize || errorImgFormat}
            onChange={handleUploadFiles}
            noProgressline
          >
            <UploadItem
              label={<Translate id="UPLOAD.MERCH.TEXT" />}
              dragText={<Translate id="UPLOAD.COVER.DRAG.DEFAULT" />}
              icon={<CoverArtIcon className="cover-art-icon" />}
              accept="image/*"
              status={status}
              error={error}
              btnDisabled={status === 'uploading'}
              isMultiline
              onChange={handleUploadFiles}
              onCancel={onCancel}
              multiple
              multipleFiles={props.multipleFiles}
              handleChangeView={handleChangeArtView}
              imageView={imageView}
              isArt
            />
          </Upload>
          <ListWrapper gap={3}>
            {!!images?.length &&
              images.map(
                (image) =>
                  image && (
                    <UploadListItem
                      key={image.id || image.filename}
                      text={image.filename || filename}
                      artwork={
                        <SquareImg
                          className="artwork"
                          size={28}
                          srcImg={image.imageUrl}
                        />
                      }
                      done={image.status === 'done'}
                      uploading={image.status === 'uploading'}
                      progress={image.progress}
                      error={!!image.error}
                      onRetry={() => null}
                      onReplace={
                        images.length && images.length > 1
                          ? undefined
                          : (files: FileList) => handleReplace(files, image)
                      }
                      onRemove={
                        images.length && images.length > 1
                          ? () => handleRemove(image)
                          : undefined
                      }
                      accept="image/*"
                      assetId={image.imageUrl}
                      onCancel={onCancel}
                      disableRemove={false}
                      disableSorting={true}
                    />
                  )
              )}
          </ListWrapper>
        </div>
      ) : (
        <Grid gap={20}>
          <Typography type="small" color={theme.colors.tertiary}>
            <Translate id="MERCH.SECTION.DESIGN.MANAGE.ART.TEXT" />
          </Typography>
          {props.activeImages?.length && (
            <ManageArtMerch
              updateMerchImages={props.updateMerchImages}
              images={props.activeImages}
            />
          )}
        </Grid>
      )}
    </div>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(styled(UploadMerchArtCtrl)`
  margin-top: 5px;

  .artwork,
  .cover-art-icon {
    flex-shrink: 0;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
  }
`);

const ManageText = styled(Typography)`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.secondaryHover};
  transition: color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover:not(.disabled),
  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }

  &.disabled {
    opacity: 0.25;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const Wrapper = styled(Flex)`
  margin-bottom: 5px;
`;

const ListWrapper = styled(Grid)`
  margin-top: 3px;
`;
